/**
 * @fileoverview Charge date picker component for Click&Pledge donations.
 * Handles date selection with various scheduling options including specific dates,
 * date ranges, and specific days of the month.
 */

import moment from 'moment';
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';


/**
 * Charge date picker component that handles different payment scheduling options.
 * Supports specific dates, date ranges, and specific days of the month with
 * appropriate validation and filtering.
 *
 * @param {Object} props - Component props
 * @param {Object} props.chargeDate - Charge date configuration from API
 * @param {Function} props.onDateChange - Callback function when date changes
 * @param {string} props.title - Optional title for the date picker
 * @returns {ReactElement} The charge date picker component
 */
export default function ChargeDate({ chargeDate, onDateChange, title }) {
    const { t } = useTranslation();
    const [ selectedDate, setSelectedDate ] = useState(null);
    const [ minDate, setMinDate ] = useState(null);
    const [ maxDate, setMaxDate ] = useState(null);


    /**
     * Converts MM/dd/yyyy string to Date object.
     *
     * @param {string} dateStr - Date string in MM/dd/yyyy format
     * @returns {Date|null} Parsed date object or null if invalid
     */
    const parseDate = dateStr => {
        if (!dateStr || typeof dateStr !== 'string') {
            return null;
        }
        const parsed = moment(dateStr, 'MM/DD/YYYY', true);

        if (!parsed.isValid()) {
            return null;
        }
        const date = parsed.toDate();

        return isNaN(date.getTime()) ? null : date;
    };

    /**
     * Strips time from a date object, setting it to midnight.
     *
     * @param {Date} d - Date object to strip time from
     * @returns {Date|null} Date object with time set to 00:00:00.000, or null if input invalid
     */
    const stripTime = d => {
        if (!d || !(d instanceof Date) || isNaN(d.getTime())) {
            return null;
        }
        const date = new Date(d);

        date.setHours(0, 0, 0, 0);

        return date;
    };

    // Initialize selected date and min/max limits based on API
    useEffect(() => {
        const today = new Date();

        today.setHours(0, 0, 0, 0);
        let defaultDate = null;
        let min = null;
        let max = null;

        if (chargeDate?.PaymentSchedulingOption === 'SpecificDate') {
            let specific = chargeDate?.SpecificDate ? parseDate(chargeDate.SpecificDate) : today;

            if (!specific || isNaN(specific.getTime()) || specific < today) {
                specific = today;
            }
            defaultDate = specific;
            min = specific;
            max = specific;
        } else if (chargeDate?.PaymentSchedulingOption === 'SpecificDaysOfMonth') {
            const daysStr = chargeDate?.SpecificDaysOfMonth || '';
            const days = daysStr.split(',').map(d => parseInt(d.trim(), 10))
                .filter(d => !isNaN(d) && d >= 1 && d <= 31);
            const currentDate = new Date();

            currentDate.setHours(0, 0, 0, 0); // normalize to midnight

            const year = currentDate.getFullYear();
            const month = currentDate.getMonth(); // 0-based month

            // Build all possible dates for current month
            const possibleDates = days.map(day => {
                const d = new Date(year, month, day);

                d.setHours(0, 0, 0, 0); // normalize

                return d;
            });

            // First check if today is in the list
            let firstDay = possibleDates.find(d => d.getTime() === currentDate.getTime());

            if (!firstDay) {
                // Otherwise filter future dates (strictly > today)
                const futureDates = possibleDates.filter(d => d.getTime() > currentDate.getTime());

                if (futureDates.length > 0) {
                    // Pick the earliest future date in current month
                    firstDay = futureDates.sort((a, b) => a - b)[0];
                } else {
                    // All dates in current month are past → go to next month
                    let nextMonth = month + 1;
                    let nextYear = year;

                    if (nextMonth > 11) { // handle December → January
                        nextMonth = 0;
                        nextYear++;
                    }

                    // Take the *earliest* available date from next month
                    firstDay = new Date(nextYear, nextMonth, Math.min(...days));
                    firstDay.setHours(0, 0, 0, 0);
                }
            }
            defaultDate = firstDay;
            min = currentDate; // allow from today onwards
            max = null; // no upper limit
        } else if (chargeDate?.PaymentSchedulingOption === 'SpecificDateRange') {
            let start = parseDate(chargeDate?.DateRangeStartDate);
            const end = parseDate(chargeDate?.DateRangeEndDate);
            let defaultD = parseDate(chargeDate?.DateRangeDefaultDate);

            if (!start || isNaN(start.getTime()) || start < today) {
                start = today;
            }
            if (!defaultD || isNaN(defaultD.getTime()) || defaultD < today) {
                defaultD = start || today;
            }

            min = start;
            max = end;
            defaultDate = defaultD;
        }
        if (defaultDate && defaultDate < today) {
            defaultDate = today;
        }

        // Only set state if we have a valid date
        if (defaultDate && !isNaN(defaultDate.getTime())) {
            setSelectedDate(defaultDate);
            const formattedDate = defaultDate.toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
            });

            onDateChange(formattedDate);
        } else {
            setSelectedDate(today);
            const formattedDate = today.toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
            });

            onDateChange(formattedDate);
        }

        setMinDate(min && !isNaN(min.getTime()) ? min : today);
        setMaxDate(max && !isNaN(max.getTime()) ? max : null);

    }, [ chargeDate, onDateChange ]);

    /**
     * Filters available dates based on the payment scheduling option.
     * Determines which dates should be selectable in the date picker.
     *
     * @param {Date} date - Date to check for availability
     * @returns {boolean} True if date should be selectable, false otherwise
     */
    const filterDate = date => {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
            return false;
        }

        const today = stripTime(new Date());
        const strippedDate = stripTime(date);

        if (!strippedDate) {
            return false;
        }

        if (chargeDate?.PaymentSchedulingOption === 'SpecificDate') {
            let specific = stripTime(chargeDate?.SpecificDate ? parseDate(chargeDate.SpecificDate) : today);

            if (!specific || specific.getTime() < today.getTime()) {
                specific = today;
            }

            return strippedDate.getTime() === specific.getTime();
        }

        if (chargeDate?.PaymentSchedulingOption === 'SpecificDaysOfMonth') {
            const daysStr = chargeDate?.SpecificDaysOfMonth || '';
            const days = daysStr.split(',').map(d => parseInt(d.trim(), 10))
                .filter(d => !isNaN(d));

            return days.includes(strippedDate.getDate()) && strippedDate.getTime() >= (today ? today.getTime() : 0);
        }

        if (chargeDate?.PaymentSchedulingOption === 'SpecificDateRange') {
            const start = stripTime(parseDate(chargeDate?.DateRangeStartDate));
            const end = stripTime(parseDate(chargeDate?.DateRangeEndDate));

            if (!start || !end) {
                return strippedDate.getTime() >= (today ? today.getTime() : 0);
            }

            const minAllowed = today && today > start ? today : start;

            return strippedDate.getTime() >= minAllowed.getTime() && strippedDate.getTime() <= end.getTime();
        }

        return false;
    };

    return (
        <div
            className = { 'form_input_wrapper form_input_wrapper_custom d-flex justify-content-between charge-date-container' }>
            <div>
                <label htmlFor = 'charge_date'>{title ? title : t('clickandpledge.chargeOn')}</label>
            </div>
            <div className = { 'w_half' }>
                <DatePicker
                    className = 'form-input'
                    dateFormat = 'MM/dd/yyyy'
                    filterDate = { filterDate }
                    maxDate = { maxDate }
                    minDate = { minDate }
                    onChange = { date => {
                        const effectiveDate = !date || isNaN(date.getTime()) ? new Date() : date;

                        setSelectedDate(effectiveDate);
                        const formattedDate = effectiveDate.toLocaleDateString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric'
                        });

                        onDateChange(formattedDate);
                    } }
                    placeholderText = { t('clickandpledge.selectDate') }
                    popperClassName = 'custom-datepicker-popper cp-datepicker'
                    selected = { selectedDate } />
            </div>
        </div>
    );
}
